import React, { createContext, useState, useEffect } from 'react';
import { CSVParser, ParsedConfigData, TradingData } from '../helpers/CSVParser';
import TradingMetricsCalculator from '../helpers/TradingMetricsCalculator';
import { OverallTradingMetrics, YearlyTradingMetrics, MonthlyTradingMetrics, DailyTradingMetrics, WeeklyTradingMetrics, initialOverallTradingMetrics } from '../interfaces/Metrics';

export interface FileData {
  id: number;
  customId: string;
  name: string;
  lines: number;
  content: string;
  include: boolean;
  initialCapital: number;
  parsedData: TradingData[];
  setDelete?: boolean;
  parsedConfig?: ParsedConfigData;
  configFileName?: string; // Add configFileName attribute
  timestamp?: number; // Add timestamp attribute
}

export interface Dataset {
  id: string;
  name: string;
  include: boolean;
  files: FileData[];
  useDeepBacktest: boolean;
  customStartDate: Date | null; // Add customStartDate attribute
  customEndDate: Date | null; // Add customEndDate attribute
}

interface DataContextProps {
  datasets: Dataset[] | undefined;
  activeDatasetId: string;
  addDataset: (name: string) => void;
  removeDataset: (datasetId: string) => void;
  setActiveDataset: (datasetId: string) => void;
  toggleDatasetInclude: (datasetId: string) => void; // Add toggleDatasetInclude function
  updateDatasets: (updatedDatasets: Dataset[]) => void; // Add updateDatasets function
  addFiles: (fileData: FileData[]) => void; // Update to accept multiple files
  removeFile: (fileName: string) => void;
  updateFiles: (updatedFiles: FileData[]) => void;
  clearFiles: () => void;
  getOverallMetrics: (customId?: string, initialCapital?: number) => OverallTradingMetrics;
  getYearlyTradingMetrics: (customId?: string, initialCapital?: number) => YearlyTradingMetrics[];
  getMonthlyTradingMetrics: (customId?: string, initialCapital?: number) => MonthlyTradingMetrics[];
  getDailyTradingMetrics: (customId?: string, initialCapital?: number) => DailyTradingMetrics[];
  getWeeklyTradingMetrics: (customId?: string, initialCapital?: number) => WeeklyTradingMetrics[];
  getAllTrades: () => TradingData[];
  getTotalInitialCapital: () => number;
  dataset: Dataset;
}

const initialDataset: Dataset = { id: '', name: '', include: false, files: [], useDeepBacktest: false, customStartDate: null, customEndDate: null }; // Initialize customStartDate and customEndDate

export const DataContext = createContext<DataContextProps>({
  datasets: [],
  activeDatasetId: '',
  addDataset: () => {},
  removeDataset: () => {},
  setActiveDataset: () => {},
  toggleDatasetInclude: () => {}, // Add toggleDatasetInclude function
  updateDatasets: () => {}, // Add updateDatasets function
  addFiles: () => {},
  removeFile: () => {},
  updateFiles: () => {},
  clearFiles: () => {},
  getOverallMetrics: () => initialOverallTradingMetrics,
  getYearlyTradingMetrics: () => [],
  getMonthlyTradingMetrics: () => [],
  getDailyTradingMetrics: () => [],
  getWeeklyTradingMetrics: () => [],
  getAllTrades: () => [],
  getTotalInitialCapital: () => 0,
  dataset: initialDataset,
});

interface DataProviderProps {
  children: React.ReactNode;
}

export const DataProvider: React.FC<DataProviderProps> = ({ children }) => {
  const [datasets, setDatasets] = useState<Dataset[]>();
  const [activeDatasetId, setActiveDatasetId] = useState<string>('default');

  useEffect(() => {
    const savedDatasets = localStorage.getItem('datasets');
    if (savedDatasets) {
      const parsedDatasets: Dataset[] = JSON.parse(savedDatasets);
      // parse csv content again to get computable onbjects
      parsedDatasets.map((dataset: Dataset) => {
        dataset.files = dataset.files.map((file: FileData) => {
          file.parsedData = CSVParser.parse(file.content);
          return file;
        });
        return dataset;
      });
      setDatasets(parsedDatasets);
      setActiveDatasetId(parsedDatasets.find(dataset => dataset.include)?.id || parsedDatasets[0].id);
    } else {
      // Create a default dataset if none exists
      const newDataset: Dataset = { id: Date.now().toString(), name: 'Default', files: [], include: true, useDeepBacktest: false, customStartDate: null, customEndDate: null }; // Set include to true
      localStorage.setItem('datasets', JSON.stringify([newDataset]));
      setDatasets([newDataset]);
      setActiveDatasetId(newDataset.id);
    }
  }, []);

  useEffect(() => {
    if (datasets) {
      // Save datasets to local storage
      localStorage.setItem('datasets', JSON.stringify(datasets));
    }
  }, [datasets]);

  const addDataset = (name: string) => {
    const newDataset: Dataset = { id: Date.now().toString(), name, files: [], include: false, useDeepBacktest: false, customStartDate: null, customEndDate: null }; // Initialize customStartDate and customEndDate
    setDatasets([...(datasets || []), newDataset]);
  };

  const removeDataset = (datasetId: string) => {
    const updatedDatasets = datasets?.filter(dataset => dataset.id !== datasetId);
    setDatasets(updatedDatasets);
    if (activeDatasetId === datasetId && updatedDatasets && updatedDatasets.length > 0) {
      setActiveDatasetId(updatedDatasets[0].id);
      updatedDatasets[0].include = true; // Set the first remaining dataset as included
    }
  };

  const setActiveDataset = (datasetId: string) => {
    const updatedDatasets = datasets?.map(dataset => 
      dataset.id === datasetId ? { ...dataset, include: true } : { ...dataset, include: false }
    );
    if (updatedDatasets) {
      updateDatasets(updatedDatasets);
    }
    setActiveDatasetId(datasetId);
  };

  const toggleDatasetInclude = (datasetId: string) => {
    setDatasets(datasets?.map(dataset => 
      dataset.id === datasetId ? { ...dataset, include: !dataset.include } : { ...dataset, include: false }
    ));
  };

  const updateDatasets = (updatedDatasets: Dataset[]) => {
    setDatasets(updatedDatasets);
  };

  useEffect(() => {
    const activeDataset = datasets?.find(dataset => dataset.id === activeDatasetId);
    if (activeDataset && activeDataset.files.some(file => file.setDelete)) {
      const updatedFiles = activeDataset.files.filter(file => !file.setDelete);
      setDatasets(datasets?.map(dataset => 
        dataset.id === activeDatasetId ? { ...dataset, files: updatedFiles } : dataset
       ));
    }
  }, [datasets, activeDatasetId]);

  const addFiles = (newFiles: FileData[]) => {
    setDatasets(datasets?.map(dataset => 
      dataset.id === activeDatasetId ? { ...dataset, files: [...dataset.files, ...newFiles] } : dataset
    ));
  };

  const removeFile = (id: string) => {
    const activeDataset = datasets?.find(dataset => dataset.id === activeDatasetId);
    if (activeDataset) {
      const preparedEntry = activeDataset.files.find(file => file.customId === id);
      if (preparedEntry) {
        preparedEntry.setDelete = true;
        setDatasets(datasets?.map(dataset => 
          dataset.id === activeDatasetId ? { ...dataset, files: [ ...dataset.files.filter(file => file.customId !== id), preparedEntry ] } : dataset
        ));
      }
    }
  };

  const updateFiles = (updatedFiles: FileData[]) => {
    setDatasets(datasets?.map(dataset => 
      dataset.id === activeDatasetId ? { ...dataset, files: updatedFiles } : dataset
    ));
  };

  const clearFiles = () => {
    setDatasets(datasets?.map(dataset => 
      dataset.id === activeDatasetId ? { ...dataset, files: [] } : dataset
    ));
  };

  const getCalculator = (customId?: string, initialCapital?: number) => {
    const activeDataset = datasets?.find(dataset => dataset.id === activeDatasetId);
    if (!activeDataset) return new TradingMetricsCalculator([]);
    let tradingData: TradingData[] = [];
    if (customId) {
      const includedFiles = activeDataset.files.filter(file => file.customId === customId);
      tradingData = includedFiles[0].parsedData || [];
      tradingData.sort((a, b) => a.datetime.getTime() - b.datetime.getTime());
      return new TradingMetricsCalculator(tradingData);
    } else {
      const includedFiles = activeDataset.files.filter(file => file.include && file.parsedData);
      tradingData = includedFiles.flatMap(file => file.parsedData).filter((data): data is TradingData => data !== undefined);
      tradingData.sort((a, b) => a.datetime.getTime() - b.datetime.getTime());
      return !initialCapital ? new TradingMetricsCalculator(tradingData) : new TradingMetricsCalculator(tradingData, true, initialCapital);
    }
  }

  const getOverallMetrics = (customId?: string, initialCapital?: number): OverallTradingMetrics => {
    return getCalculator(customId, initialCapital).calculateOverallMetrics();
  };

  const getYearlyTradingMetrics = (customId?: string, initialCapital?: number): YearlyTradingMetrics[] => {
    return getCalculator(customId, initialCapital).calculateYearlyMetrics();
  };

  const getMonthlyTradingMetrics = (customId?: string, initialCapital?: number): MonthlyTradingMetrics[] => {
    return getCalculator(customId, initialCapital).calculateMonthlyMetrics();
  };

  const getDailyTradingMetrics = (customId?: string, initialCapital?: number): DailyTradingMetrics[] => {
    return getCalculator(customId, initialCapital).calculateDailyMetrics();
  };

  const getWeeklyTradingMetrics = (customId?: string, initialCapital?: number): WeeklyTradingMetrics[] => {
    return getCalculator(customId, initialCapital).calculateWeeklyMetrics();
  };

  const getAllTrades = () => {
    const activeDataset = datasets?.find(dataset => dataset.id === activeDatasetId);
    if (!activeDataset) return [];
    const includedFiles = activeDataset.files.filter(file => file.include && file.parsedData);
    const recalculatedTrades = TradingMetricsCalculator.joinAndRecalculateTradeLists(includedFiles);
    return recalculatedTrades;
  };

  const getTotalInitialCapital = () => {
    const activeDataset = datasets?.find(dataset => dataset.id === activeDatasetId);
    if (!activeDataset) return 0;
    const includedFiles = activeDataset.files.filter(file => file.include && file.parsedData);
    return includedFiles.reduce((acc, file) => acc + file.initialCapital, 0);
    // return includedFiles.reduce((acc, file) => acc + TradingMetricsCalculator.calculateInitialCapital(file.parsedData || []), 0);
  };

  return (
    <DataContext.Provider value={{ datasets, activeDatasetId, addDataset, removeDataset, setActiveDataset, toggleDatasetInclude, updateDatasets, addFiles, removeFile, updateFiles, clearFiles, getOverallMetrics, getYearlyTradingMetrics, getMonthlyTradingMetrics, getDailyTradingMetrics, getWeeklyTradingMetrics, getTotalInitialCapital, getAllTrades, dataset: datasets?.find(dataset => dataset.id === activeDatasetId) || initialDataset }}>
      {children}
    </DataContext.Provider>
  );
};
